/**
 * Тела решений человека, которые выпускают или отзывают ключ: одобрение, пакетное одобрение,
 * отзыв. Перенос в другой слот и отправка в тестовый канал живут рядом со своими ответами —
 * `operations.ts` (`MoveDraftBodySchema`) и `transport.ts` (`CreateTestSendBodySchema`).
 *
 * До 2026-10-25 эти три тела разбирал бэкенд по месту (`closedObject` в `listeners/ui.ts`), а
 * интерфейс собирал их литералами. Схемами они стали вместе с полями изоляции брендов: поле,
 * которое обязано совпасть у двух сторон, описывается один раз.
 *
 * ИЗОЛЯЦИЯ БРЕНДОВ (решение Ильи 2026-10-25, docs/ai-lab.md; находки ревизии изоляции).
 * Ключ подписывает бренд из СТРОКИ черновика и канал из конфига ЭТОГО бренда
 * (docs/multi-brand.md §2, замок 1). Это верно по построению, но ничего не говорит о том, что
 * видел человек: экран, открытый по адресу одного бренда на черновике другого, показывал бы
 * канал и режим чужого бренда, и одобрение ушло бы туда, чего человек не видел. Поэтому тело
 * несёт то, что было на экране:
 *
 *   • `expected_brand` — бренд ЭКРАНА, на котором человек принимает решение (бренд адреса
 *     страницы, в шапке и теме которого открыт диалог). Не бренд модели рендера: модель называет
 *     бренд той же строки черновика, и сверка с ней не могла бы не сойтись. После перехода
 *     экрана черновика на адрес его бренда (`DraftPanel`) бренды равны, и сверка ловит экран,
 *     оставшийся без перехода;
 *   • `expected_chat_id` — канал, который диалог назвал (канал текущего режима бренда).
 *
 * Бэкенд сверяет оба ДО любой записи и до вызова демона подписи и отказывает
 * 409 `state_conflict` с `details.reason` `brand_mismatch` или `channel_mismatch` — константы
 * ниже. Поля ОБЯЗАТЕЛЬНЫ: вкладка со старым бандлом получит 400 и перезагрузит экран, а не
 * одобрит вслепую.
 */

import * as z from 'zod';
import { BRAND_RE, CHAT_ID_RE, CONTENT_HASH_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';
import { PublicationDispatchStateSchema } from './operations.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);
const ContentHash = z.string().regex(CONTENT_HASH_RE);

/** Бренд, которому человек принимает решение. Форма — `BRAND_RE`, как у подписанного ключа. */
export const ExpectedBrandSchema = z.string().regex(BRAND_RE);
/** Канал, который назвал диалог. Пустого канала здесь нет: в пустой канал одобрить нельзя. */
export const ExpectedChatIdSchema = z.string().regex(CHAT_ID_RE);

// ─────────────────────────────────────────────────────────────────────────────
// `details.reason` отказов изоляции брендов (409 `state_conflict` / 422)
//
// Слово одно на бэкенд и интерфейс — тот же приём, что `BRAND_AGENTS_OFF_REASON` (`ui.ts`):
// диалог, читающий всякий 409 как «черновик изменился», по нему отличает отказ бренда и
// показывает текст сервера.

/** Черновик принадлежит не тому бренду, что назвал запрос (`expected_brand`). */
export const BRAND_MISMATCH_REASON = 'brand_mismatch';
/** Канал, который подписал бы бэкенд, — не тот, что назвал диалог (`expected_chat_id`). */
export const CHANNEL_MISMATCH_REASON = 'channel_mismatch';
/** Канал не входит в каналы бренда (`publishableChannels` бренда и профиля). */
export const CHANNEL_NOT_OF_BRAND_REASON = 'channel_not_of_brand';
/**
 * Источник материала не подключён к бренду (пакет материалов прогона мониторинга или тема): нет
 * пары `brand_source` (бренд, код). Проверяется существование пары, включённость не
 * перепроверяется (`assertSourcesOfBrand` бэкенда). Кода нет в реестре вовсе — другой отказ,
 * `unknown_source`.
 */
export const SOURCE_NOT_OF_BRAND_REASON = 'source_not_of_brand';

export const BRAND_ISOLATION_REASONS = [
  BRAND_MISMATCH_REASON,
  CHANNEL_MISMATCH_REASON,
  CHANNEL_NOT_OF_BRAND_REASON,
  SOURCE_NOT_OF_BRAND_REASON,
] as const;
export type BrandIsolationReason = (typeof BRAND_ISOLATION_REASONS)[number];

/**
 * Журнал демона подписи закрепил черновик за другим брендом (`DRAFT_BRAND_MISMATCH` демона, его
 * проверка 4б): база и журнал демона разошлись — восстановленная копия, правка руками. Это дело
 * оператора, не экрана: бэкенд уже сверил бренд экрана с брендом строки черновика, и обновление
 * экрана отказ не снимет. Поэтому слово своё, не `brand_mismatch`, и в
 * `BRAND_ISOLATION_REASONS` его нет — это не сверка экрана с запросом.
 */
export const SIGNER_DRAFT_BRAND_PINNED_REASON = 'signer_draft_brand_pinned';

/**
 * Исход работы публикации (`publication_job.outcome_code`), закрытой бэкендом при выдаче:
 * подписанный канал работы не входит в каналы её бренда. Верхний регистр — словарь исходов
 * работы и вердиктов публикатора, а не `details.reason` HTTP-отказа.
 */
export const CHANNEL_NOT_OF_BRAND_OUTCOME = 'CHANNEL_NOT_OF_BRAND';

/**
 * Исход работы, чьё отправленное сообщение вернулось из другого чата (отчёт попытки публикатора,
 * `returned_message.chat.id` ≠ `publication_job.chat_id`). Работа и версия уходят в
 * `needs_manual_check`: пост где-то есть, но не там, где подписан, — решает человек. Словарь тот
 * же, что у `CHANNEL_NOT_OF_BRAND_OUTCOME`; фразу экрана держит `phrases.ts` интерфейса.
 */
export const SENT_TO_WRONG_CHAT_OUTCOME = 'SENT_TO_WRONG_CHAT';

// ─────────────────────────────────────────────────────────────────────────────
// POST /api/v1/drafts/:id/versions/:n/approve

/**
 * `expected_mode` по-прежнему необязателен (`ApproveInput.expectedMode` бэкенда): канал
 * теперь называет `expected_chat_id`, и он строже режима — режим совпадёт и тогда, когда
 * канал режима сменили в конфиге между показом диалога и нажатием.
 */
export const ApproveBodySchema = z.strictObject({
  /** Момент слота дословно из ответа расписания (09 §9.4). */
  slot_at: IsoUtcMs,
  expected_content_hash: ContentHash,
  note: z.string().max(2000).nullable().optional(),
  expected_mode: PublicationDispatchStateSchema.optional(),
  expected_brand: ExpectedBrandSchema,
  expected_chat_id: ExpectedChatIdSchema,
});
export type ApproveBody = z.output<typeof ApproveBodySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// POST /api/v1/batch/approve

/**
 * Потолок пакета. Двадцать — четыре недели расписания GeoBoost с запасом; выше начинается
 * массовая заливка, где каждый элемент — вызов демона подписи и выпущенный ключ. Бэкенд
 * (`domain/batch-approve.ts`) и экран пакета держали по своей копии числа; эта — общая.
 */
export const BATCH_APPROVE_MAX_ITEMS = 20;

export const BatchApproveItemSchema = z.strictObject({
  draft_id: Uuid,
  version_no: z.int().min(1).max(100_000),
  slot_at: IsoUtcMs,
  expected_content_hash: ContentHash,
});
export type BatchApproveItem = z.output<typeof BatchApproveItemSchema>;

/**
 * `expected_brand` и `expected_chat_id` — НА ПАКЕТ, а не на элемент, и бэкенд сверяет их с
 * КАЖДЫМ элементом. Пакет — это неделя одного бренда (экран пакета открыт в бренде), и канал
 * у него один — тот, что назвала плашка режима. Поле на элемент разрешило бы пакет из постов
 * двух брендов, у которого нет экрана и который никто не видел целиком; поле на пакет делает
 * такой пакет невыразимым. Расхождение любого элемента — отказ всего пакета до первой
 * подписи (`brand_mismatch` / `channel_mismatch` со списком виноватых `draft_id`).
 */
export const BatchApproveBodySchema = z.strictObject({
  items: z.array(BatchApproveItemSchema).min(1).max(BATCH_APPROVE_MAX_ITEMS),
  expected_mode: PublicationDispatchStateSchema.optional(),
  expected_brand: ExpectedBrandSchema,
  expected_chat_id: ExpectedChatIdSchema,
});
export type BatchApproveBody = z.output<typeof BatchApproveBodySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// POST /api/v1/drafts/:id/versions/:n/retract

/**
 * Отзыв подписывает ключ `revoke` на канал, куда пост УШЁЛ (`publication_job.chat_id`), а не
 * на канал режима, — поэтому `expected_chat_id` здесь нет: диалог отзыва канал не выбирает.
 * `expected_brand` (бренд экрана) есть: удалить сообщение из канала чужого бренда, глядя на
 * экран своего, — ровно та ошибка, ради которой поле заведено.
 */
export const RetractBodySchema = z.strictObject({
  reason: z.string().min(1).max(500),
  expected_brand: ExpectedBrandSchema,
});
export type RetractBody = z.output<typeof RetractBodySchema>;
